import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "./EventCard";
import type { Event } from "~/server/directus.server";
import { getEventLink } from "~/utils";
import { LuckyButton } from "./LuckyButton";

type SameYearEventsProps = {
  year: number;
  currentEventId: string;
  locale: string;
};

export function SameYearEvents({ year, currentEventId, locale }: SameYearEventsProps) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSameYearEvents = async () => {
      try {
        const response = await fetch(`/api/events/year/${year}?eventId=${currentEventId}&locale=${locale}`);
        const data = await response.json();
        setEvents(data.events);
      } catch (error) {
        console.error('Failed to fetch same year events:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSameYearEvents();
  }, [year, currentEventId, locale]);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-64 bg-slate-700 rounded-lg mb-4"></div>
        <div className="h-64 bg-slate-700 rounded-lg"></div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center mt-12">
        <p className="text-lg text-slate-400 mb-4">
          {t('events.noSameYearEvents', { year })}
        </p>
        <LuckyButton />
      </div>
    );
  }

  return (
    <div className="mt-12">
      <h2 className="text-2xl font-semibold mb-6">
        {t('events.sameYear', { year })}
      </h2>
      <div className="flex flex-col gap-6">
        {events.map((event) => (
          <Card
            key={event.id}
            date={`${event.year}`}
            title={event.translations[0].title}
            description={event.translations[0].header}
            image={`https://cdn.whatwhen.day/${event.image?.filename_disk}`}
            status={event.translations[0].status}
            to={getEventLink(event)}
          />
        ))}
      </div>
    </div>
  );
} 